import PropTypes from 'prop-types';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import useMenus from 'actions/useMenus';

import Button from 'components/field/Button';
import ComponentErrorBoundary from 'components/global/ComponentErrorBoundary';
import NoRecordsFound from 'components/global/NoRecordsFound';

import useAccessControl from 'hooks/useAccessControl';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocalStorage from 'hooks/useLocalStorage';
import useMediaQuery from 'hooks/useMediaQuery';
import useUpdateEffect from 'hooks/useUpdateEffect';
import useWindowSize from 'hooks/useWindowSize';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

import { faScrewdriverWrench } from '@fortawesome/pro-regular-svg-icons';

import { FilterType, convertFilterValueToDTO, convertSortValueToDTO } from './DynamicListFilterUtils';
import DynamicListMapView from './DynamicListMapView/DynamicListMapView';
import DynamicListTable, { DynamicListComponentMap } from './DynamicListTable';
import { setDynamicListData } from './dynamicListSlice';

/**
 * @author @buunsal
 */
const DynamicList = (props) => (
	<ComponentErrorBoundary
		fallback={
			<NoRecordsFound
				lottieName="alert-error-icon"
				title="dynamicList.errorBoundaryTitle"
				subTitle={null}
				actionButton={
					<Button
						icon={faScrewdriverWrench}
						label="cleanCache"
						onClick={() => {
							window?.localStorage?.removeItem(`${props.id}_filters`);
							window?.localStorage?.removeItem(`${props.id}_columns`);
							window?.localStorage?.removeItem(`${props.id}_hidden_columns`);
							window?.localStorage?.removeItem(`${props.id}_pageSize`);
							window?.location?.reload();
						}}
					/>
				}
			/>
		}>
		<DynamicListComponent {...props} />
	</ComponentErrorBoundary>
);

const DynamicListComponent = (props) => {
	const {
		id,
		actionObjectType,
		columns: columnsProp,
		fetchUrl,
		defaultFilters,
		defaultSort,
		noRecordsFound,
		showHeader = true,
		showFilters = true,
		showHeaderFilters = true,
		exportName,
		onSelectionChange,
		showCheckbox,
		showMapViewButton = false,
		sendRequestWithColumns = true,
		ignoreCaseSensitive = false,
		exportNameTranslationObject,
		infiniteScroll = false,
		disableInfiniteScrollUp = false,
		showColumnCalculation: showColumnCalculationProp = true,
		requestParameters,
		selectedRows
	} = props;

	const dispatch = useDispatch();

	const setReduxData = (props) => {
		dispatch(setDynamicListData({ id, data: props }));
	};

	const sliceData = useSelector((state) => state.dynamicListData);
	const dynamiclistData = sliceData[id];
	const globalUpdate = sliceData.update;

	const { data, size: dataSize = 0, update: localUpdate } = dynamiclistData ?? sliceData ?? {};
	const update = globalUpdate || localUpdate;

	const axiosPrivate = useAxiosPrivate();
	const { t } = useTranslation();
	const [errorMsg, setErrorMsg] = useState(null);

	const isMapViewState = useSelector((state) => state?.layout?.listMapView);
	const isMapView = errorMsg ? false : isMapViewState ? showMapViewButton : false;

	const actionsMenu = useMenus(id, actionObjectType);

	const [currentPage, setCurrentPage] = useState(1);
	const [latLng, setLatLng] = useState({ lat: null, lng: null });

	useUpdateEffect(() => {
		if (!isMapView) {
			// Reset latLng when leaving map view
			setLatLng({ lat: null, lng: null });
		}
	}, [isMapView]);

	const [storedPageSize, setStoredPageSize] = useLocalStorage(`${id}_pageSize`, 10);
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const { windowHeight } = useWindowSize();

	const infiniteScrollPageSize = useMemo(() => {
		if (isMobile) {
			if (windowHeight < 600) return 20;
			if (windowHeight < 850) return 30;
			if (windowHeight < 1100) return 40;
			return 50;
		} else {
			if (windowHeight < 1100) return 20;
			if (windowHeight < 1500) return 30;
			if (windowHeight < 1900) return 40;
			return 50;
		}
	}, [windowHeight, isMobile]);
	const pageSize = infiniteScroll ? infiniteScrollPageSize : storedPageSize;
	const [isLoading, setIsLoading] = useState(true);
	const [isLoadedOnce, setIsLoadedOnce] = useState(false);
	const infiniteScrollTopLoading = useRef(false);
	const infiniteScrollBottomLoading = useRef(false);
	const [noMoreInfiniteScrollRecords, setNoMoreInfiniteScrollRecords] = useState(false);
	const [infiniteScrollUpdateCounter, setInfiniteScrollUpdateCounter] = useState(0);

	const [saveDynamicListFilters] = useLocalStorage('saveDynamicListFilters', true);

	const isAuthorized = useAccessControl();
	const columns = useMemo(() => columnsProp.filter((column) => isAuthorized(column.roleName)), [columnsProp]);
	const [storedColumns, setStoredColumns] = useLocalStorage(`${id}_columns`, columns);
	const [hiddenColumns, setHiddenColumns] = useLocalStorage(
		`${id}_hidden_columns`,
		columns.filter((column) => column.show === false).map((column) => column.accessor)
	);

	useEffect(() => {
		if (JSON.stringify(storedColumns) === JSON.stringify(columns)) {
			window?.localStorage?.removeItem(`${id}_columns`);
			window?.localStorage?.removeItem(`${id}_hidden_columns`);
			return;
		}

		const newColumns = [];
		storedColumns.forEach((column) => {
			const foundColumn = columns.find((col) => col.accessor === column.accessor);
			if (foundColumn) {
				const isExist = newColumns.findIndex((col) => col.accessor === column.accessor) === -1;
				if (!isExist) return;

				if (JSON.stringify({ ...foundColumn, show: true }) !== JSON.stringify({ ...column, show: true })) {
					newColumns.push(foundColumn);
				} else {
					newColumns.push(column);
				}
			}
		});

		columns.forEach((column, index) => {
			const foundColumn = newColumns.find((col) => col.accessor === column.accessor);
			if (!foundColumn) {
				newColumns.splice(index, 0, column); // add column
			}
		});

		if (JSON.stringify(storedColumns) !== JSON.stringify(newColumns)) {
			setStoredColumns(newColumns);
			setHiddenColumns(newColumns.filter((column) => column.show === false).map((column) => column.accessor));
		}
	}, [columns]);

	const requiredFields = useMemo(() => {
		if (!sendRequestWithColumns) return [];
		const newRequiredFieldsSet = new Set(['ID']); // ID is always required

		actionsMenu?.forEach((menu) => {
			menu.items.forEach((item) => {
				if (item.requiredFields) {
					item.requiredFields.forEach((field) => newRequiredFieldsSet.add(field));
				}
			});
		});

		storedColumns
			?.filter((col) => (isMapView ? col?.showOnMap === true : col?.show !== false))
			.forEach((col) => {
				newRequiredFieldsSet.add(col.accessor);
				const { renderCell } = col;
				if (renderCell) {
					const requiredFields = DynamicListComponentMap[renderCell].requiredFields;
					if (requiredFields) {
						requiredFields?.forEach((field) => newRequiredFieldsSet.add(field));
					}
				}
			});

		return Array.from(newRequiredFieldsSet);
	}, [storedColumns, isMapView]);

	const [requiredFieldsState, setRequiredFieldsState] = useState(requiredFields);

	useUpdateEffect(() => {
		if (JSON.stringify(requiredFieldsState) !== JSON.stringify(requiredFields)) {
			const isCover = requiredFields.every((el) => requiredFieldsState.includes(el));
			!isCover && setRequiredFieldsState(requiredFields);
		}
	}, [requiredFields]);

	const savedFilters = useMemo(() => JSON.parse(window.localStorage.getItem(`${id}_filters`)), []);
	const [filters, setFilters] = useState(defaultFilters ?? (saveDynamicListFilters ? (savedFilters ?? []) : []));

	useUpdateEffect(() => {
		setFilters(defaultFilters ?? []);
	}, [defaultFilters]);

	const [selected, setSelected] = useState(selectedRows || []);

	useUpdateEffect(() => {
		if (selectedRows) {
			setSelected(selectedRows);
		}
	}, [selectedRows]);

	useEffect(() => {
		if (onSelectionChange) {
			onSelectionChange(selected);
		}
	}, [selected]);

	const [sortBy, setSortBy] = useState(defaultSort ? convertSortValueToDTO(defaultSort) : undefined);

	useUpdateEffect(() => {
		if (defaultSort) {
			setSortBy(convertSortValueToDTO(defaultSort));
		} else {
			setSortBy(undefined);
		}
	}, [defaultSort]);

	const fetchData = async (abortController) => {
		(!infiniteScroll || !isLoadedOnce) && setIsLoading(true);

		let requestJson = {
			paging: { offset: (currentPage - 1) * pageSize, count: pageSize },
			filter:
				filters?.length > 0
					? filters.map((filter) => convertFilterValueToDTO(filter, ignoreCaseSensitive))
					: undefined,
			sort: sortBy ?? undefined,
			columns: sendRequestWithColumns ? requiredFieldsState : undefined,
			totalRecord: currentPage === 1 && !infiniteScroll,
			...requestParameters
		};

		if (isMapView) {
			requestJson = {
				paging: { offset: 0, count: 100 },
				filter: [
					{
						key: ['lat'],
						action: 'between',
						from: latLng.lat.from,
						to: latLng.lat.to
					},
					{
						key: ['lng'],
						action: 'between',
						from: latLng.lng.from,
						to: latLng.lng.to
					}
				],
				columns: sendRequestWithColumns ? [...requiredFieldsState, 'lat', 'lng'] : undefined,
				totalRecord: false,
				...requestParameters
			};
		}

		await axiosPrivate
			.post(fetchUrl, requestJson, {
				signal: abortController?.signal
			})
			.then((response) => {
				if (response?.status === 200) {
					/**
					 * If the user is on the last page and the last page is empty, then the user is redirected to the calculated last page.
					 */
					if (response?.data?.data?.length === 0 && currentPage > 1 && !infiniteScroll) {
						const newPage = Math.ceil(response?.data?.size / pageSize) || 1;
						setCurrentPage(newPage);
						setReduxData({
							size: response?.data?.size
						});

						return;
					}
					if (infiniteScroll && currentPage > 1) {
						setReduxData({ data: [...data, ...response.data.data] });
					} else {
						setReduxData({ ...response.data });
					}

					if (infiniteScroll) {
						if (response.data?.data?.length < pageSize) {
							setNoMoreInfiniteScrollRecords(true);
						} else {
							setNoMoreInfiniteScrollRecords(false);
						}

						if (infiniteScrollBottomLoading.current) {
							infiniteScrollBottomLoading.current = false;
						}

						if (infiniteScrollTopLoading.current) {
							infiniteScrollTopLoading.current = false;
						}
						setIsLoadedOnce(true);
					}
					errorMsg && setErrorMsg(null);
					setIsLoading(false);
				} else {
					setReduxData({ data: [], size: 0, offset: 0 });
					setErrorMsg('alert.anErrorOccurred');
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				if (!abortController?.signal.aborted) {
					setReduxData({ data: [], size: 0, offset: 0 });
					setErrorMsg(
						t(error.response?.data?.code, {
							fields: error.response?.data?.data?.requiredFields?.map((field) => t(`${field}`)).join(', ')
						})
					);
					AppLogger.error(error);
				}
			});
	};

	useEffect(() => {
		if (isMapView && (!latLng.lat || !latLng.lng)) return;

		const abortController = new AbortController();

		fetchData(abortController);

		return () => {
			abortController && abortController.abort();
		};
	}, [
		currentPage,
		pageSize,
		sortBy,
		filters,
		infiniteScrollUpdateCounter,
		requestParameters,
		requiredFieldsState,
		latLng
	]);

	useEffect(() => {
		if (update) {
			if (infiniteScroll && currentPage !== 1) {
				setCurrentPage(1);
			} else {
				fetchData();
			}
			setSelected([]);
		}
	}, [update]);

	const onFilterModelChange = useCallback((value) => {
		if (infiniteScroll) {
			setIsLoading(true);
		}
		setFilters(value);
		setCurrentPage(1);
	}, []);

	useUpdateEffect(() => {
		if (filters?.length > 0) window.localStorage.setItem(`${id}_filters`, JSON.stringify(filters));
		else window.localStorage.removeItem(`${id}_filters`);
	}, [filters]);

	useEffect(
		() => () => {
			if (defaultFilters) window.localStorage.removeItem(`${id}_filters`);
		},
		[]
	);

	useUpdateEffect(() => {
		setSelected([]);
	}, [isMapView]);

	const handleInfiniteScrollTop = () => {
		if (infiniteScrollTopLoading.current) return;
		infiniteScrollTopLoading.current = true;
		if (currentPage === 1) {
			setInfiniteScrollUpdateCounter((infiniteScrollUpdateCounter) => infiniteScrollUpdateCounter + 1);
		} else {
			setCurrentPage(1);
		}
	};

	const handleInfiniteScrollBottom = () => {
		if (infiniteScrollBottomLoading.current) return;
		infiniteScrollBottomLoading.current = true;
		setCurrentPage((prev) => prev + 1);
	};

	const showColumnCalculation = useMemo(() => {
		if (!showColumnCalculationProp) return false;
		return storedColumns.some(
			(col) => col.show !== false && (col.filterType === FilterType.number || col.filterType === FilterType.float)
		);
	}, [showColumnCalculationProp, storedColumns]);

	return (
		<>
			{isMapView ? (
				<DynamicListMapView
					data={data}
					columns={columns}
					loading={isLoading}
					filterModel={filters}
					actionsMenu={actionsMenu}
					setLatLng={setLatLng}
				/>
			) : (
				<DynamicListTable
					id={id}
					fetchUrl={fetchUrl}
					data={data}
					dataSize={dataSize}
					selectionModel={selected}
					onSelectionModelChange={setSelected}
					currentPage={currentPage}
					onPageChange={setCurrentPage}
					pageSize={pageSize}
					onPageSizeChange={setStoredPageSize}
					sortModel={sortBy}
					onSortModelChange={setSortBy}
					filterModel={filters}
					onFilterModelChange={onFilterModelChange}
					defaultSort={defaultSort}
					errorMsg={errorMsg}
					noRecordsFound={noRecordsFound}
					loading={isLoading}
					showHeader={showHeader}
					actionObjectType={actionObjectType}
					exportName={exportName}
					showCheckbox={showCheckbox}
					showMapViewButton={showMapViewButton}
					columns={storedColumns}
					originalColumns={columns}
					setColumns={setStoredColumns}
					hiddenColumns={hiddenColumns}
					setHiddenColumns={setHiddenColumns}
					actionsMenu={actionsMenu}
					showFilters={showFilters}
					showHeaderFilters={showHeaderFilters}
					exportNameTranslationObject={exportNameTranslationObject}
					infiniteScroll={infiniteScroll}
					isInfiniteScrollBottomLoading={infiniteScrollBottomLoading.current}
					isInfiniteScrollTopLoading={infiniteScrollTopLoading.current}
					noMoreInfiniteScrollRecords={noMoreInfiniteScrollRecords}
					handleInfiniteScrollTop={handleInfiniteScrollTop}
					handleInfiniteScrollBottom={handleInfiniteScrollBottom}
					disableInfiniteScrollUp={disableInfiniteScrollUp}
					showColumnCalculation={showColumnCalculation}
					requestParameters={requestParameters}
				/>
			)}
		</>
	);
};

DynamicList.propTypes = {
	/** Required key for store dynamiclist data */
	id: PropTypes.string.isRequired,
	/** Object type for actions menu. */
	actionObjectType: PropTypes.string,
	/** Required definition array of columns. */
	columns: PropTypes.array.isRequired,
	/** Required url for fetch data from apis. */
	fetchUrl: PropTypes.string.isRequired,
	/** Optional default filters array. */
	defaultFilters: PropTypes.array,
	/** Optional default sort array. */
	defaultSort: PropTypes.array,
	/** Overrides default NoRecordsFound. */
	noRecordsFound: PropTypes.object,
	/** If set false, header does not render. */
	showHeader: PropTypes.bool,
	/** If set false, filters does not render. */
	showFilters: PropTypes.bool,
	/** If set false, header filters does not render. */
	showHeaderFilters: PropTypes.bool,
	/** Name of the exported file. */
	exportName: PropTypes.string,
	/** Callback function for selection change. */
	onSelectionChange: PropTypes.func,
	/** If set true, checkbox column renders. */
	showCheckbox: PropTypes.bool,
	/** If set true, map view button renders. */
	showMapViewButton: PropTypes.bool,
	/** If set true, send request with columns. */
	sendRequestWithColumns: PropTypes.bool,
	/** If set true, ignore case sensitivity */
	ignoreCaseSensitive: PropTypes.bool,
	/** Object for export name translation */
	exportNameTranslationObject: PropTypes.object,
	/** If set true, infinite scroll will be enabled */
	infiniteScroll: PropTypes.bool,
	/** If set false, top scroll will not load the data */
	disableInfiniteScrollUp: PropTypes.bool,
	/** If set true, column calculation will be enabled */
	showColumnCalculation: PropTypes.bool,
	/** Request parameters */
	requestParameters: PropTypes.object,
	/** Selected rows */
	selectedRows: PropTypes.array
};

export default memo(DynamicList);
